import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { API_SERVICE } from "../Services/CommonApi";
import { commonPayloadFields } from "../helper/storage";
import {
  GET_FINANCE_CAPABILITY,
  GET_ACCOUNT_CODE_TREE,
  GET_ACCOUNT_CODES,
  ADD_EDIT_FINANCE_ACCOUNT_CODE,
  DELETE_FINANCE_ACCOUNT_CODE,
  GET_ACCOUNT_MAPPINGS,
  ADD_EDIT_ACCOUNT_MAPPING,
  DELETE_ACCOUNT_MAPPING,
  GET_UNMAPPED_SOURCES,
  RESOLVE_ACCOUNT,
} from "../constant/CommonApiUrl";

// Account-code/GL-structure API. Response quirks: writes resolve to a bare
// integer id, GetAccountCodeById to an array of one, and messageCode
// "204"/"FEATURE_DISABLED" mean success, not an error. Thunks resolve with
// the full envelope so callers can branch on messageCode themselves.

export const ACCOUNT_SOURCE_TYPES = [
  { value: "STORE_CATEGORY", label: "Store Category" },
  { value: "PARTS", label: "Parts" },
  { value: "OIL", label: "Oil Products" },
  { value: "PRODUCT_GENERAL", label: "Book / Chart" },
];

// messageCode -> human copy. Unknown codes fall back to the server's own `message`.
const ACCOUNT_CODE_MESSAGES = {
  DUPLICATE_CODE: "That code already exists under this section.",
  SELF_REFERENCE: "An account can't be its own parent.",
  INVALID_PARENT: "Selected parent account no longer exists — refresh and try again.",
  NOT_FOUND: "Account not found.",
  HAS_CHILDREN: "This section still has sub-accounts under it — remove or reassign them first.",
  MAX_DEPTH_EXCEEDED: "The selected parent is already a sub-account — the chart doesn't go any deeper.",
  TYPE_MISMATCH: "This account's type must match its parent's type.",
  CYCLE_DETECTED: "That parent is a descendant of this account — can't create a loop.",
  NOT_POSTABLE: "That account can't be posted to (not marked as postable).",
  HAS_MAPPINGS: "This account is still mapped to items/categories — remove those mappings first.",
  DUPLICATE_MAPPING: "That item/category is already mapped for this vessel.",
  INVALID_SOURCE_TYPE: "Unsupported source type.",
  FEATURE_DISABLED: "Managed chart of accounts isn't enabled for this tenant.",
  UNMAPPED_ITEM: "No account mapping exists yet for this item.",
};

export const getAccountCodeMessage = (messageCode, fallbackMessage) =>
  ACCOUNT_CODE_MESSAGES[messageCode] || fallbackMessage || "Something went wrong.";

// Flattens the tree, tagging each node with its depth.
export const flattenAccountTree = (nodes = [], depth = 0, out = []) => {
  nodes.forEach((node) => {
    out.push({ ...node, depth });
    if (node.children?.length) flattenAccountTree(node.children, depth + 1, out);
  });
  return out;
};

// Mirrors postable_only=true's server-side pruning, computed client-side
// from the already-fetched tree so pickers don't need a second call.
export const flattenPostableLeaves = (nodes = []) =>
  flattenAccountTree(nodes).filter((n) => n.allow_purchase_posting === "Y");

const FETCH_CAPABILITY = "financeAccount/fetchCapability";
const FETCH_TREE = "financeAccount/fetchTree";
const SAVE_ACCOUNT_CODE = "financeAccount/saveAccountCode";
const REMOVE_ACCOUNT_CODE = "financeAccount/removeAccountCode";
const FETCH_MAPPINGS = "financeAccount/fetchMappings";
const SAVE_MAPPING = "financeAccount/saveMapping";
const REMOVE_MAPPING = "financeAccount/removeMapping";
const FETCH_UNMAPPED = "financeAccount/fetchUnmapped";
const RESOLVE_ACCOUNT_ACTION = "financeAccount/resolveAccount";
const FETCH_ACCOUNT_CODES_GRID = "financeAccount/fetchAccountCodesGrid";

export const fetchFinanceCapability = createAsyncThunk(
  FETCH_CAPABILITY,
  async (_arg, thunkAPI) => {
    try {
      const response = await API_SERVICE.get(GET_FINANCE_CAPABILITY);
      return response.data.result;
    } catch (err) {
      return thunkAPI.rejectWithValue(err.message);
    }
  },
);

export const fetchAccountCodeTree = createAsyncThunk(
  FETCH_TREE,
  async (params = {}, thunkAPI) => {
    try {
      const response = await API_SERVICE.get(GET_ACCOUNT_CODE_TREE, params);
      return response.data.result;
    } catch (err) {
      return thunkAPI.rejectWithValue(err.message);
    }
  },
);

export const saveAccountCode = createAsyncThunk(
  SAVE_ACCOUNT_CODE,
  async (payload, thunkAPI) => {
    try {
      const response = await API_SERVICE.post(ADD_EDIT_FINANCE_ACCOUNT_CODE, {
        ID: payload.id || 0,
        ...payload,
        ...commonPayloadFields,
      });
      return response.data.result;
    } catch (err) {
      return thunkAPI.rejectWithValue(err.message);
    }
  },
);

export const removeAccountCode = createAsyncThunk(
  REMOVE_ACCOUNT_CODE,
  async ({ id }, thunkAPI) => {
    try {
      const response = await API_SERVICE.del(DELETE_FINANCE_ACCOUNT_CODE, {
        id,
        updatedby: commonPayloadFields.updatedby,
      });
      return response.data.result;
    } catch (err) {
      return thunkAPI.rejectWithValue(err.message);
    }
  },
);

export const fetchAccountMappings = createAsyncThunk(
  FETCH_MAPPINGS,
  async (params, thunkAPI) => {
    try {
      const response = await API_SERVICE.get(GET_ACCOUNT_MAPPINGS, params);
      return { source_type: params.source_type, ...response.data.result };
    } catch (err) {
      return thunkAPI.rejectWithValue(err.message);
    }
  },
);

export const saveAccountMapping = createAsyncThunk(
  SAVE_MAPPING,
  async (payload, thunkAPI) => {
    try {
      const response = await API_SERVICE.post(ADD_EDIT_ACCOUNT_MAPPING, {
        id: 0,
        vessel_id: null,
        ...payload,
        createdBy: commonPayloadFields.createdby,
      });
      return response.data.result;
    } catch (err) {
      return thunkAPI.rejectWithValue(err.message);
    }
  },
);

export const removeAccountMapping = createAsyncThunk(
  REMOVE_MAPPING,
  async ({ id }, thunkAPI) => {
    try {
      const response = await API_SERVICE.del(DELETE_ACCOUNT_MAPPING, {
        id,
        updatedby: commonPayloadFields.updatedby,
      });
      return response.data.result;
    } catch (err) {
      return thunkAPI.rejectWithValue(err.message);
    }
  },
);

// Fetched on demand per tab, not eagerly — OIL alone can be 3,700+ unpaged rows.
export const fetchUnmappedSources = createAsyncThunk(
  FETCH_UNMAPPED,
  async (params, thunkAPI) => {
    try {
      const response = await API_SERVICE.get(GET_UNMAPPED_SOURCES, params);
      return { source_type: params.source_type, ...response.data.result };
    } catch (err) {
      return thunkAPI.rejectWithValue(err.message);
    }
  },
);

// Per-line GL preview, called as soon as an item is picked. Deliberately has
// no extraReducers case below — the result is read directly at the call site
// via .unwrap(), not stored in shared state (it's a transient, per-selection
// preview, not app-wide data like the mapping/tree fetches above).
export const resolveAccount = createAsyncThunk(
  RESOLVE_ACCOUNT_ACTION,
  async (params, thunkAPI) => {
    try {
      const response = await API_SERVICE.get(RESOLVE_ACCOUNT, params);
      return response.data.result;
    } catch (err) {
      return thunkAPI.rejectWithValue(err.message);
    }
  },
);

// Paged flat grid for Settings > Finance > Account Codes' "Account List" tab —
// distinct from fetchAccountCodeTree above, which recursive pickers use.
// GetAccountCodes is ORDER BY ID DESC and paged, so it can't be assembled
// into a tree; keep them as separate data sources rather than deriving one
// from the other.
export const fetchAccountCodesGrid = createAsyncThunk(
  FETCH_ACCOUNT_CODES_GRID,
  async (params, thunkAPI) => {
    try {
      const response = await API_SERVICE.get(GET_ACCOUNT_CODES, params);
      return response.data.result;
    } catch (err) {
      return thunkAPI.rejectWithValue(err.message);
    }
  },
);

const initialState = {
  capability: null, // { managed_account_code, gl_code_separator, gl_code_max_depth, gl_code_default_suffix, account_count }
  capabilityStatus: "idle", // idle | loading | loaded | error

  tree: [],
  treeMessageCode: null,
  treeStatus: "idle",

  mappingsByType: {}, // source_type -> array
  mappingsMessageCodeByType: {},

  unmappedByType: {}, // source_type -> array, populated on demand
  unmappedLoadedTypes: [], // which types have been fetched at least once

  accountCodesGrid: [],
  accountCodesGridTotalData: 0,
  accountCodesGridStatus: "idle", // idle | loading | loaded | error

  lastError: null, // { messageCode, message } from the most recent write/delete
};

const financeAccountSlice = createSlice({
  name: "financeAccount",
  initialState,
  reducers: {
    clearFinanceAccountError: (state) => {
      state.lastError = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchFinanceCapability.pending, (state) => {
        state.capabilityStatus = "loading";
      })
      .addCase(fetchFinanceCapability.fulfilled, (state, action) => {
        state.capability = action.payload.data;
        state.capabilityStatus = "loaded";
      })
      .addCase(fetchFinanceCapability.rejected, (state) => {
        state.capabilityStatus = "error";
      })

      .addCase(fetchAccountCodeTree.pending, (state) => {
        state.treeStatus = "loading";
      })
      .addCase(fetchAccountCodeTree.fulfilled, (state, action) => {
        // 204/FEATURE_DISABLED arrive with status:true + empty data — not an error.
        state.tree = action.payload.data || [];
        state.treeMessageCode = action.payload.messageCode;
        state.treeStatus = "loaded";
      })
      .addCase(fetchAccountCodeTree.rejected, (state) => {
        state.treeStatus = "error";
      })

      .addCase(saveAccountCode.fulfilled, (state, action) => {
        if (!action.payload.status) {
          state.lastError = {
            messageCode: action.payload.messageCode,
            message: action.payload.message,
          };
        } else {
          state.lastError = null;
        }
      })

      .addCase(removeAccountCode.fulfilled, (state, action) => {
        state.lastError = action.payload.status
          ? null
          : { messageCode: action.payload.messageCode, message: action.payload.message };
      })

      .addCase(fetchAccountMappings.fulfilled, (state, action) => {
        const { source_type, data, messageCode } = action.payload;
        state.mappingsByType[source_type] = data || [];
        state.mappingsMessageCodeByType[source_type] = messageCode;
      })

      .addCase(saveAccountMapping.fulfilled, (state, action) => {
        state.lastError = action.payload.status
          ? null
          : { messageCode: action.payload.messageCode, message: action.payload.message };
      })

      .addCase(removeAccountMapping.fulfilled, (state, action) => {
        state.lastError = action.payload.status
          ? null
          : { messageCode: action.payload.messageCode, message: action.payload.message };
      })

      .addCase(fetchUnmappedSources.fulfilled, (state, action) => {
        const { source_type, data } = action.payload;
        state.unmappedByType[source_type] = data || [];
        if (!state.unmappedLoadedTypes.includes(source_type)) {
          state.unmappedLoadedTypes.push(source_type);
        }
      })

      .addCase(fetchAccountCodesGrid.pending, (state) => {
        state.accountCodesGridStatus = "loading";
      })
      .addCase(fetchAccountCodesGrid.fulfilled, (state, action) => {
        state.accountCodesGrid = action.payload.data || [];
        state.accountCodesGridTotalData = action.payload.totalData || 0;
        state.accountCodesGridStatus = "loaded";
      })
      .addCase(fetchAccountCodesGrid.rejected, (state) => {
        state.accountCodesGridStatus = "error";
      });
  },
});

export const { clearFinanceAccountError } = financeAccountSlice.actions;
export default financeAccountSlice.reducer;
